import { useParams, Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import { topics, topicDetails } from '@/mocks/topics';
import TopicHeader from './components/TopicHeader';
import TopicBody from './components/TopicBody';
import DiscussionSection from './components/DiscussionSection';
import RelatedTopics from './components/RelatedTopics';

export default function TopicDetailPage() {
  const { id } = useParams();
  const topic = topics.find((item) => String(item.id) === id);
  const detail = topic ? topicDetails.find((item) => item.topicId === topic.id) : undefined;

  useSeo({
    title: topic ? `${topic.title} - ${topic.categoryName}` : '话题未找到',
    description: topic
      ? topic.summary
      : '抱歉，这个话题暂时不存在或已被下架，欢迎回到话题广场浏览其他热议内容。',
    keywords: topic ? [topic.categoryName, ...topic.tags].join(',') : '话题广场,热议话题',
    path: topic ? `/topic/${topic.id}` : '/topics',
    type: 'article',
    jsonLd: topic
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: topic.title,
              description: topic.summary,
              articleSection: topic.categoryName,
              keywords: topic.tags.join(','),
              inLanguage: 'zh-CN',
              author: { '@type': 'Organization', name: topic.editor },
              publisher: { '@type': 'Organization', name: '瓜田观察' },
              datePublished: '2026-09-10',
              dateModified: '2026-09-10',
              mainEntityOfPage: `${window.location.origin}/topic/${topic.id}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
                { '@type': 'ListItem', position: 2, name: '话题广场', item: `${window.location.origin}/topics` },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: topic.title,
                  item: `${window.location.origin}/topic/${topic.id}`,
                },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!topic || !detail) {
    return (
      <SiteLayout>
        <section className="flex min-h-[60vh] items-center justify-center bg-background-50 px-4">
          <div className="text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个话题走丢了</h1>
            <p className="mt-3 text-sm text-foreground-600">
              它可能已被合并、下架，或者链接里的编号有误。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-block whitespace-nowrap bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              回到话题广场
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-4 md:px-8">
          <Breadcrumb
            items={[
              { label: '话题广场', to: '/topics' },
              { label: topic.categoryName, to: `/topics?category=${topic.categoryId}` },
              { label: topic.title },
            ]}
          />
        </div>
      </div>
      <TopicHeader topic={topic} />
      <TopicBody detail={detail} />
      <DiscussionSection topicId={topic.id} topicTitle={topic.title} />
      <RelatedTopics currentId={topic.id} categoryId={topic.categoryId} />
    </SiteLayout>
  );
}